import React, { useState, useEffect, useCallback } from 'react';
import { FaTrash, FaDownload, FaRedo, FaSearch, FaFilter } from 'react-icons/fa';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getServerURL } from '../config/api';
import './StockTrack.css';

const StockTrack = () => {
    const [stockData, setStockData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('newest');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);
    const { latestScan } = useWebSocket();

    // Fetch stock data from database
    const fetchStockData = useCallback(async (isBackground = false) => {
        if (!isBackground) {
            setLoading(true);
        } else {
            setIsRefreshing(true);
        }

        try {
            const serverURL = getServerURL();
            const token = localStorage.getItem('robridge_token');

            const response = await fetch(`${serverURL}/api/stock-track?limit=1000&t=${Date.now()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();

            if (data.success) {
                const stock = data.stock || [];
                setStockData(stock);
            } else {
                console.error('API returned error:', data.error);
                setStockData([]);
            }
        } catch (error) {
            console.error('Error fetching stock data:', error);
            setStockData([]);
        } finally {
            if (!isBackground) {
                setLoading(false);
            } else {
                setTimeout(() => setIsRefreshing(false), 500);
            }
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchStockData(false);
    }, [fetchStockData]);

    // Auto-refresh every 15 seconds (background)
    useEffect(() => {
        const interval = setInterval(() => {
            fetchStockData(true);
        }, 15000);
        return () => clearInterval(interval);
    }, [fetchStockData]);

    // Real-time update when new scan arrives
    useEffect(() => {
        if (latestScan) {
            console.log('📡 Stock Track: New scan detected via WebSocket, refreshing...', latestScan);
            // Use a small delay to ensure backend has completed the database write
            setTimeout(() => {
                fetchStockData(true);
            }, 500);
        }
    }, [latestScan]);

    // Export to CSV
    const exportToCSV = () => {
        const csvContent = [
            ['Barcode Data', 'Status', 'FIFO Status', 'Date', 'Time'],
            ...filteredStockData.map(item => {
                const date = new Date(item.created_at);
                return [
                    item.barcode_data,
                    item.status || 'IN',
                    fifoStatuses[item.id] || 'PENDING',
                    date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
                    date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                ];
            })
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const date = new Date().toISOString().split('T')[0];
        const a = document.createElement('a');
        a.href = url;
        a.download = `stock_track_${date}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Clear all stock track data
    const clearAllStockData = async () => {
        if (!window.confirm('Are you sure you want to clear all stock track data? This action cannot be undone.')) {
            return;
        }

        try {
            const serverURL = getServerURL();
            const token = localStorage.getItem('robridge_token');

            const response = await fetch(`${serverURL}/api/stock-track/clear`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (data.success) {
                console.log(`Cleared ${data.deletedCount} stock track records`);
                // Refresh the data
                fetchStockData(false);
            } else {
                console.error('Failed to clear stock track data:', data.error);
                alert('Failed to clear stock track data');
            }
        } catch (error) {
            console.error('Error clearing stock track data:', error);
            alert('Error clearing stock track data');
        }
    };

    // Fix table structure (remove UNIQUE constraint)
    const fixTableStructure = async () => {
        if (!window.confirm('This will recreate the stock_track table and clear all existing data. Continue?')) {
            return;
        }

        try {
            const serverURL = getServerURL();
            const token = localStorage.getItem('robridge_token');

            const response = await fetch(`${serverURL}/api/stock-track/fix-table`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (data.success) {
                alert('Table fixed successfully! You can now scan barcodes multiple times.');
                // Refresh the data
                fetchStockData(false);
            } else {
                console.error('Failed to fix table:', data.error);
                alert('Failed to fix table: ' + data.error);
            }
        } catch (error) {
            console.error('Error fixing table:', error);
            alert('Error fixing table: ' + error.message);
        }
    };


    // Calculate FIFO status for each row
    const calculateFIFOStatus = (stockData) => {
        // Sort ALL entries by created_at (oldest first)
        const sortedEntries = [...stockData].sort((a, b) =>
            new Date(a.created_at) - new Date(b.created_at)
        );

        // Calculate FIFO status for each entry
        const fifoStatuses = {};

        sortedEntries.forEach((entry, index) => {
            if (entry.status === 'IN') {
                // All IN items are PENDING
                fifoStatuses[entry.id] = 'PENDING';
            } else if (entry.status === 'OUT') {
                // Check if ALL older entries (before this index) also went OUT
                const olderEntries = sortedEntries.slice(0, index);
                const hasOlderIn = olderEntries.some(e => e.status === 'IN');

                // If any older entry is still IN, this is a FIFO violation
                fifoStatuses[entry.id] = hasOlderIn ? 'WARNING' : 'OK';
            }
        });

        return fifoStatuses;
    };


    // Calculate FIFO statuses
    const fifoStatuses = calculateFIFOStatus(stockData);

    // Count violations
    const violationCount = Object.values(fifoStatuses).filter(status => status === 'WARNING').length;

    // Filter and sort data
    const filteredStockData = stockData
        .filter(item =>
            item.barcode_data.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredStockData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredStockData.length / itemsPerPage);

    return (
        <div className="scanned-barcodes-page">
            <div className="page-header">
                <h1>Stock Track</h1>
                <p>Monitor scanned inventory items with timestamps</p>
            </div>

            {/* Controls Section */}
            <div className="controls-section">
                <div className="search-filter-container">
                    <div className="search-input">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search barcode data..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                    <div className="filter-dropdown">
                        <FaFilter className="filter-icon" />
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                        >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                        </select>
                    </div>
                </div>
                <div className="action-buttons">
                    <button
                        className="btn btn-secondary"
                        onClick={() => fetchStockData(false)}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <div className="spinner-small" style={{
                                    width: '14px',
                                    height: '14px',
                                    border: '2px solid #E3821E',
                                    borderTopColor: 'transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }}></div>
                                Loading...
                            </>
                        ) : (
                            <>
                                <FaRedo />
                                Refresh
                            </>
                        )}
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={exportToCSV}
                        disabled={filteredStockData.length === 0}
                    >
                        <FaDownload />
                        Export CSV
                    </button>
                    <button
                        className="btn btn-danger"
                        onClick={clearAllStockData}
                        disabled={stockData.length === 0}
                    >
                        <FaTrash />
                        Clear All
                    </button>
                </div>
            </div>

            <div className="scanned-barcodes-content">
                <div className="barcodes-summary">
                    <div className="summary-info">
                        <h2>Stock Track History</h2>
                        <span className="total-count">
                            {loading ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                    <div className="spinner-small" style={{
                                        width: '14px',
                                        height: '14px',
                                        border: '2px solid #E3821E',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                    Loading...
                                </span>
                            ) : `${filteredStockData.length} of ${stockData.length} scanned barcodes`}
                            {!loading && totalPages > 1 && (
                                <span className="page-info"> • Page {currentPage} of {totalPages}</span>
                            )}
                        </span>
                    </div>
                </div>

                <div className="barcodes-table-container">
                    <table className="barcodes-table">
                        <thead>
                            <tr>
                                <th>Barcode Data</th>
                                <th>IN/OUT</th>
                                <th>FIFO Status</th>
                                <th>Scanned Date</th>
                                <th>Scanned Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="loading-cell" style={{
                                        textAlign: 'center',
                                        padding: '60px 40px',
                                        color: '#5F6368'
                                    }}>
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '12px'
                                        }}>
                                            <div className="spinner-icon"></div>
                                            <span>Loading scanned barcodes...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : currentItems.length > 0 ? (
                                currentItems.map((item, index) => (
                                    <tr
                                        key={`${item.id}-${index}-${Date.now()}`}
                                        className="barcode-row"
                                    >
                                        <td className="barcode-data">
                                            <span className="barcode-text">
                                                {item.barcode_data}
                                            </span>
                                        </td>
                                        <td className="status-cell">
                                            <span className={`status-badge status-${item.status?.toLowerCase()}`}>
                                                {item.status || 'IN'}
                                            </span>
                                        </td>
                                        <td className="fifo-cell">
                                            <span className={`fifo-badge fifo-${fifoStatuses[item.id]?.toLowerCase()}`}>
                                                {fifoStatuses[item.id] || 'PENDING'}
                                            </span>
                                        </td>
                                        <td className="barcode-date">
                                            {new Date(item.created_at).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric'
                                            })}
                                        </td>
                                        <td className="barcode-time">
                                            {new Date(item.created_at).toLocaleTimeString('en-GB', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5">
                                        <div className="empty-state">
                                            <div className="empty-icon">
                                                <FaSearch />
                                            </div>
                                            <h3>No Stock Data Found</h3>
                                            <p>
                                                {searchTerm
                                                    ? 'No stock data matches your current search criteria.'
                                                    : 'No barcodes have been scanned yet. Start scanning with your ESP32 device to see stock history here.'}
                                            </p>
                                            {searchTerm && (
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => {
                                                        setSearchTerm('');
                                                        setSortOrder('newest');
                                                    }}
                                                >
                                                    Clear Filters
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    {!loading && totalPages > 1 && (
                        <div className="pagination">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="btn btn-secondary"
                            >
                                Previous
                            </button>
                            <span className="page-info">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="btn btn-secondary"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Refreshing Indicator */}
            {isRefreshing && (
                <div className="fixed-loader" style={{
                    position: 'fixed',
                    bottom: '24px',
                    left: '300px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    padding: '8px 16px',
                    borderRadius: '24px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    zIndex: 1000,
                    border: '1px solid #E8EAED',
                    fontSize: '13px',
                    color: '#5F6368',
                    fontWeight: 500
                }}>
                    <div className="spinner-small" style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid #E3821E',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    Updating codes...
                </div>
            )}
        </div>
    );
};

export default StockTrack;
